/**
 * Pagination Utilities
 * Calculates page layouts for multi-page invoice rendering
 *
 * Strategy: Estimate page breaks BEFORE rendering to determine
 * which items go on each page, then render accordingly
 */

import type { Invoice } from '@shared/schemas/invoice.schema';
import type { PaperSize } from '@/types/paper';
import { getPaperSizeInPx } from '@/types/paper';

export interface PageLayout {
  pageNumber: number;
  startItemIndex: number;
  endItemIndex: number; // exclusive
  showHeader: boolean;
  showFromTo: boolean;
  showDates: boolean;
  showTotals: boolean;
  showNotes: boolean;
  showFooter: boolean;
}

/**
 * Estimate the height of invoice sections in pixels
 * These are approximate values based on the compact invoice design
 */
const SECTION_HEIGHTS = {
  header: 110,        // Compact header (text-2xl)
  fromSection: 95,    // From/To sections
  dates: 50,          // Invoice dates
  tableHeader: 35,    // Table header row
  tableRow: 28,       // Table data row
  totals: 160,        // Totals section
  notes: 65,          // Notes section
  footer: 45,         // Footer
} as const;

const SPACING = {
  base: 24,           // Base padding
  section: 15,        // Between sections
  small: 12,          // Small spacing
  afterTable: 15,     // After table before totals
};

/**
 * Calculate page layouts by estimating how many items fit per page
 * This runs BEFORE rendering to determine what should go on each page
 */
export function calculatePageLayouts(
  invoice: Invoice,
  paperSize: PaperSize,
  totalPages: number
): PageLayout[] {
  // If totalPages is 1, everything fits on one page
  if (totalPages === 1) {
    return [{
      pageNumber: 1,
      startItemIndex: 0,
      endItemIndex: invoice.lineItems.length,
      showHeader: true,
      showFromTo: true,
      showDates: true,
      showTotals: true,
      showNotes: !!invoice.notes,
      showFooter: true,
    }];
  }

  const paperPx = getPaperSizeInPx(paperSize);
  const availableHeight = paperPx.height - SPACING.base * 2; // Account for padding

  console.log('[calculatePageLayouts] Calculating for', totalPages, 'pages', {
    availableHeight,
    totalItems: invoice.lineItems.length,
  });

  const layouts: PageLayout[] = [];
  let currentPage = 1;
  let currentItemIndex = 0;
  let currentHeight = SPACING.base;

  // Helper to add a new page
  const startNewPage = () => {
    layouts.push({
      pageNumber: currentPage,
      startItemIndex: currentItemIndex,
      endItemIndex: currentItemIndex,
      showHeader: true,
      showFromTo: currentPage === 1,
      showDates: currentPage === 1,
      showTotals: false,
      showNotes: false,
      showFooter: false,
    });
    currentHeight = SPACING.base;
  };

  // Helper to check if content fits
  const canFit = (height: number) => {
    return currentHeight + height + SPACING.section < availableHeight;
  };

  // Start first page
  startNewPage();

  // Add header
  if (canFit(SECTION_HEIGHTS.header)) {
    currentHeight += SECTION_HEIGHTS.header + SPACING.section;
  }

  // Add from/to (page 1 only)
  if (currentPage === 1 && canFit(SECTION_HEIGHTS.fromSection)) {
    currentHeight += SECTION_HEIGHTS.fromSection + SPACING.section;
  }

  // Add dates (page 1 only)
  if (currentPage === 1 && canFit(SECTION_HEIGHTS.dates)) {
    currentHeight += SECTION_HEIGHTS.dates + SPACING.small;
  }

  // Add table header
  if (canFit(SECTION_HEIGHTS.tableHeader)) {
    currentHeight += SECTION_HEIGHTS.tableHeader;
  }

  // Distribute line items across pages
  while (currentItemIndex < invoice.lineItems.length) {
    if (canFit(SECTION_HEIGHTS.tableRow)) {
      currentHeight += SECTION_HEIGHTS.tableRow;
      currentItemIndex++;
      layouts[layouts.length - 1].endItemIndex = currentItemIndex;
    } else {
      // Start new page for remaining items
      currentPage++;
      startNewPage();

      // Add header and table header to new page
      if (canFit(SECTION_HEIGHTS.header)) {
        currentHeight += SECTION_HEIGHTS.header + SPACING.section;
      }
      if (canFit(SECTION_HEIGHTS.tableHeader)) {
        currentHeight += SECTION_HEIGHTS.tableHeader;
      }

      // Add the first item
      if (canFit(SECTION_HEIGHTS.tableRow)) {
        currentHeight += SECTION_HEIGHTS.tableRow;
        currentItemIndex++;
        layouts[layouts.length - 1].endItemIndex = currentItemIndex;
      }
    }
  }

  // Add spacing before totals
  currentHeight += SPACING.afterTable;

  // Add totals to last page
  const lastPage = layouts[layouts.length - 1];
  if (canFit(SECTION_HEIGHTS.totals)) {
    lastPage.showTotals = true;
    currentHeight += SECTION_HEIGHTS.totals;
  } else {
    // Need new page for totals
    currentPage++;
    layouts.push({
      pageNumber: currentPage,
      startItemIndex: invoice.lineItems.length,
      endItemIndex: invoice.lineItems.length,
      showHeader: true,
      showFromTo: false,
      showDates: false,
      showTotals: true,
      showNotes: !!invoice.notes,
      showFooter: true,
    });
    currentHeight = SPACING.base + SECTION_HEIGHTS.header + SECTION_HEIGHTS.totals;
  }

  // Add notes and footer to last page
  const finalPage = layouts[layouts.length - 1];
  if (invoice.notes && canFit(SECTION_HEIGHTS.notes)) {
    finalPage.showNotes = true;
    currentHeight += SECTION_HEIGHTS.notes + SPACING.section;
  }
  finalPage.showFooter = true;

  console.log('[calculatePageLayouts] Result:', {
    totalPages: layouts.length,
    layouts: layouts.map(l => ({
      page: l.pageNumber,
      items: `${l.startItemIndex}-${l.endItemIndex}`,
      itemCount: l.endItemIndex - l.startItemIndex,
      hasTotals: l.showTotals,
    })),
  });

  return layouts;
}
